import { ResizeMode } from '@wix/thumbnail-generator';
import classnames from 'classnames';
import React, { FC, useMemo } from 'react';
import {
  getPictureUrl,
  getWixSiteMediaTokenFromFolderId,
  isAllWixMediaRootFolder,
  isSecureFile,
} from '../../../../../../utils/media-manager-sdk/items';
import { isWixSite } from '../../../../../../utils/media-manager-sdk/sources';
import {
  isFailed as isFileFailed,
  isTranscoding as isFileTranscoding,
} from '../../../../../../utils/media-manager-sdk/transcoding';
import { ResizedImage } from '../../../../index';
import { ErrorOverlay, TranscodingOverlay } from '../../../../TranscodingOverlays';
import styles from '../../FileThumbnail.scss';
import { IOuter } from './PictureThumbnail.types';

export const PictureThumbnail: FC<IOuter> = ({
  file,
  className,
  wrapperClassName,
  width,
  height,
  dataHook = 'image-file-thumbnail',
  params = {},
  thumbnailBackground,
  style = {},
  saveRef,
  shouldUsePictureBackgroundImageImmediately,
}) => {
  const { isTranscoding, isFailed, siteMediaToken, isAllUserBasedMedia } = useMemo(() => {
    const { folderId, source } = file;

    return {
      isTranscoding: isFileTranscoding(file),
      isFailed: isFileFailed(file),
      siteMediaToken: isWixSite(source) ? getWixSiteMediaTokenFromFolderId(folderId) : null,
      isAllUserBasedMedia: isAllWixMediaRootFolder(folderId),
    };
  }, [file]);

  const resizeImageStyle = useMemo(
    () => ({ ...style, backgroundColor: thumbnailBackground }),
    [style, thumbnailBackground],
  );
  const resizeImageParams = useMemo(() => ({ mode: ResizeMode.Fit, ...params }), [params]);

  return (
    <div className={classnames(styles.thumbnailWrapper, wrapperClassName)} ref={saveRef}>
      <ResizedImage
        style={resizeImageStyle}
        className={classnames(styles.thumbnail, styles.picture, className)}
        src={getPictureUrl(file, width, height)}
        siteMediaToken={siteMediaToken}
        isAllUserBasedMedia={isAllUserBasedMedia}
        source={file.source}
        isSecure={isSecureFile(file)}
        width={width}
        height={height}
        params={resizeImageParams}
        dataHook={dataHook}
        shouldUseBackgroundImageImmediately={shouldUsePictureBackgroundImageImmediately}
      />

      {isTranscoding && <TranscodingOverlay />}
      {isFailed && <ErrorOverlay />}
    </div>
  );
};
